import { useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  FormGroup,
  Checkbox,
  FormControlLabel,
  TextField,
  Collapse,
} from "@mui/material";

import Grid from "@mui/material/Grid2";
import {
  customerCart,
  getAddonsListByRegion,
  getAddressListWithDeliveryCharge,
  getDeliveryFeeData,
  updateAddonQuantity,
  updateCartMessage,
  updateCartQuantity,
  updateComboCartQuantity,
  updateBulkCartQuantity,
} from "../../utilities/service";
import { useAuth } from "../../hooks/AuthContext";
import { Link, useNavigate, useParams } from "react-router";
import BreadcrumbNavigation from "../../components/common/BreadcrumbNavigation";
import { CommonSection } from "../../components/common/CommonSection";
import CartItem from "./CartItem";
import { styled } from "@mui/material/styles";
import SuggestedProducts from "../../components/common/SuggestedProducts";
import AddonDialog from "./AddonDialog";
import useApiQuery from "../../hooks/useApiQuery";
import { useEffect } from "react";
import useApiMutation from "../../hooks/useApiMutation";
import { toast } from "react-toastify";
import DeliveryCard from "./DeliveryCard";
import {
  calculateTotalBill,
  generateOrderId,
  generateRandomNumber,
  getCartDisplayCount,
  processOrderBackup,
  successOrder,
  triggerRazorPay,
} from "./cartUtil";
import BillingDetails from "./BillingDetails";
import moment from "moment";
import { useCartContext } from "../../hooks/CartContext";
import AddonItem from "./AddonItem";
import CartItemSkeleton from "./skeleton/CartItemSkeleton";
import DeliveryTypeSkeleton from "./skeleton/DeliveryTypeSkeleton";
import CheckoutResult from "../checkout-result/CheckoutResult";
import CartEmpty from "./CartEmpty";
import CartSideNav from "./CartSideNav";
import AddressDialog from "./address/AddressDialog";
import TcModal from "../../components/common/TcModal";
import CustomModal from "../../components/common/CustomModal";
import LogoLoader from "../../animations/LogoLoader";
import ParentComboProduct from "./ParentComboProduct";
import { filterProductsByDeliveryType } from "./PickupLocationDropdown";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import debounce from "lodash.debounce";

function groupByParentId(details) {
  const groups = {};
  details.forEach((item) => {
    if (item.parent_id) {
      if (!groups[item.parent_id]) groups[item.parent_id] = [];
      groups[item.parent_id].push(item);
    }
  });
  return Object.entries(groups).map(([parent_id, items]) => ({
    parent_id,
    parent_product: items[0]?.parent_product_addon_details, // ✅ Fixed: was parent_product
    items,
  }));
}
export default function Cart() {
  let { sortKind, flavour } = useParams();
  let navigate = useNavigate();
  const {
    user,
    setUser,
    setAddressDialogOpen,
    addressDialogOpen,
    setSelectAddressDrawer,
  } = useAuth();
  const dialogRef = useRef(null);
  const checkoutResultDialogRef = useRef(null);

  const { applyDiscount, setApplyDiscount } = useCartContext();
  const hasInitializedCartNoteRef = useRef(false);

  const [tcOpen, setTcOpen] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [toggleModal, setToggleModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [suggestAddon, setSuggestAddon] = useState(true);
  const [showSkip, setShowSkip] = useState(true);
  const [checkoutLoader, setCheckoutLoader] = useState(false);
  const [addonItems, setAddonItems] = useState([]);
  const deliveryType = "DOOR_STEP_DELIVERY";
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [primaryAddress, setPrimaryAddress] = useState({});
  const [backupTotalBill, setBackupTotalBill] = useState(0);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [tcAgree, setTcAgree] = useState(false);
  const [gstInvoiceDetails, setGstInvoiceDetails] = useState({
    gstin: null,
    business_name: null,
  });
  const [cartCustomerNote, setCartCustomerNote] = useState("");
  const [isCustomerNoteOpen, setIsCustomerNoteOpen] = useState(false);
  const CART_NOTE_MAX_LENGTH = 50;
  const [billDetails, setBillDetails] = useState({
    totalAmount: 0,
    taxableAmount: 0,
    gstAmount: 0,
    addonTotalAmount: 0,
    cakeTotalAmount: 0,
    cakeSubTotalAfterDiscount: 0,
    cartSubTotal: 0,
    dynamicCouponSave: 0,
    bulkSubTotal: 0,
    rawCouponSave: 0,
    productDiscountSave: 0,
  });
  const {
    data,
    error,
    isLoading: cartLoading,
    refetch,
  } = useApiQuery({
    queryKey: [
      "customerCart",
      user?.customer_id,
      user?.region_id,
      // orderId && user?.cartCount,
    ],
    queryFn: () => customerCart(user?.customer_id, user?.region_id),
  });

  const {
    mutate: deliveryFeeDataRefetch,
    isPending: deliveryFeeDataIsLoading,
    error: deliveryFeeError,
    data: deliveryFeeData,
  } = useApiMutation({
    mutationFn: (customerAddressId) =>
      getDeliveryFeeData(customerAddressId, user?.region_id),
    options: {
      onSuccess: (data) => {
        if (!data?.error) {
          // console.log(data);
        } else {
          toast.error(data?.message);
        }
      },
      onError: (error) => {
        toast.error(error?.message);
      },
    },
  });
  const {
    data: addressList,
    error: addressError,
    isLoading: addressLoader,
    refetch: addressListFetch,
  } = useApiQuery({
    queryKey: [
      "getAddressListWithDeliveryCharge",
      user?.customer_id,
      user?.region_id,
    ],
    queryFn: () =>
      getAddressListWithDeliveryCharge(user?.customer_id, user?.region_id),
  });

  const { data: addonList, isLoading: addonByRegionLoader } = useApiQuery({
    queryKey: ["getAddonsListByRegion", user?.region_id],
    queryFn: () => getAddonsListByRegion(user?.region_id),
  });

  const { mutate: updateCartQtyMutate } = useApiMutation({
    mutationFn: (param) =>
      param?.parentId
        ? updateComboCartQuantity(param)
        : updateCartQuantity(param),
    options: {
      onSuccess: (data) => {
        if (!data?.error) {
          setUser((prev) => {
            return { ...prev, cartCount: data?.data?.cart_count };
          });
          // ✅ Clear coupon if cart is now empty
          if (data?.data?.cart_count === 0) {
            setApplyDiscount({
              code: null,
              percentage: null,
              type: null,
              save: null,
              min_value: null,
              max_value: null,
            });
          }
          toast.success(data?.message);
          refetch();
        } else {
          toast.error(data?.message);
        }
      },
      onError: (error) => {
        toast.error(error?.message);
        console.error("Error in update cart qty:", error);
      },
    },
  });
  const { mutate: updateAddonQtyMutate } = useApiMutation({
    mutationFn: (param) => updateAddonQuantity(param),
    options: {
      onSuccess: (data) => {
        if (!data?.error) {
          setUser((prev) => {
            return { ...prev, cartCount: data?.data?.cart_count };
          });
          toast.success(data?.message);
          refetch();
        } else {
          toast.error(data?.message);
        }
      },
      onError: (error) => {
        toast.error(error?.message);
        console.error("Error in update cart qty:", error);
      },
    },
  });
  // updateBulk
  const { mutate: updateBulkQtyMutate } = useApiMutation({
    mutationFn: (param) => updateBulkCartQuantity(param),
    options: {
      onSuccess: (data) => {
        if (!data?.error) {
          setUser((prev) => ({
            ...prev,
            cartCount: data?.data?.cart_count,
          }));
          toast.success(data?.message);
          refetch(); // ✅ refreshes cart UI
        } else {
          toast.error(data?.message);
        }
      },
      onError: (error) => {
        toast.error(error?.message);
        console.error("Error in bulk qty update:", error);
      },
    },
  });
  // let preHrsArray = [];
  // let egglessArray = [];
  // const cartList = data;
  // cartList?.data?.details?.forEach((element) => {
  //   if (element?.eggless == 0) {
  //     preHrsArray.push(element?.variations?.egg_preparation);
  //     egglessArray.push(0);
  //   } else if (element?.eggless == 1) {
  //     preHrsArray.push(element?.variations?.eggless_preparation);
  //     egglessArray.push(1);
  //   }
  // });
  // let maxPreparation = Math.max(...preHrsArray);
  // let isEggless = Math.max(...egglessArray);
  // isEggless = isEggless == 1 ? true : false;
  let preHrsArray = [];
  let egglessArray = [];
  const cartList = data;
  const comoProductSlot = {};

  // cartList?.data?.details?.forEach((element) => {
  //   const parentId = element?.parent_product?.id;

  //   if (parentId) {
  //     // Combo product: group by parent ID
  //     if (!comoProductSlot[parentId]) {
  //       comoProductSlot[parentId] = [];
  //     }
  //     comoProductSlot[parentId].push(element);
  //   } else {
  //     // Regular product
  //     if (element?.eggless == 0) {
  //       preHrsArray.push(element?.variations?.egg_preparation || 0);
  //       egglessArray.push(0);
  //     } else if (element?.eggless == 1) {
  //       preHrsArray.push(element?.variations?.eggless_preparation || 0);
  //       egglessArray.push(1);
  //     }
  //   }
  // });
  cartList?.data?.details?.forEach((element) => {
    const parentId = element?.parent_product?.id;
    const isFast = Number(element?.products?.fast_delivery) === 1; // ✅

    if (parentId) {
      if (!comoProductSlot[parentId]) comoProductSlot[parentId] = [];
      comoProductSlot[parentId].push(element);
    } else {
      if (isFast) return; // ✅ skip fast delivery products

      if (element?.eggless == 0) {
        preHrsArray.push(element?.variations?.egg_preparation || 0);
        egglessArray.push(0);
      } else if (element?.eggless == 1) {
        preHrsArray.push(element?.variations?.eggless_preparation || 0);
        egglessArray.push(1);
      }
    }
  });

  // Handle combo groups
  Object.values(comoProductSlot).forEach((group) => {
    const firstItem = group[0];
    const parentVariation = firstItem?.parent_product?.variation?.[0];

    const isEgglessCombo = group.some((item) => item?.eggless == 1);

    if (isEgglessCombo) {
      preHrsArray.push(parentVariation?.eggless_preparation || 0);
      egglessArray.push(1);
    } else {
      preHrsArray.push(parentVariation?.egg_preparation || 0);
      egglessArray.push(0);
    }
  });

  // Final values
  // let maxPreparation = Math.max(...preHrsArray);

  // ✅ empty = all fast delivery → 1hr
  let maxPreparation = preHrsArray.length > 0 ? Math.max(...preHrsArray) : 1;
  useEffect(() => {
    setAddonItems(cartList?.data?.addons || []);
    cartList?.data?.addons?.length > 0
      ? setSuggestAddon(false)
      : setSuggestAddon(true);
  }, [cartList?.data?.addons]);

  // useEffect(() => {
  //   const checkDiscountValidity = async () => {
  //     if (+billDetails?.cakeTotalAmount > 0 && +applyDiscount?.save > 0) {
  //       const originalTotal =
  //         (+billDetails?.cakeTotalAmount || 0) + (+applyDiscount?.save || 0);

  //       if (+originalTotal < +applyDiscount?.min_value) {
  //         setApplyDiscount({
  //           code: null,
  //           percentage: null,
  //           type: null,
  //           save: null,
  //           min_value: null,
  //         });
  //         toast.warning("Coupon removed since minimum value is not met!");
  //       }
  //     }
  //   };
  //   checkDiscountValidity();
  // }, [billDetails?.cakeTotalAmount, applyDiscount]);
  // ✅ Fix - use normalProductSubTotal directly (already the original price before discount)

  const addOnOpen = (from) => {
    setSuggestAddon(false);
    from == "ADDON_CARD" && setShowSkip(false);
    handleAddonDialog();
  };
  // const productsBasedOnDeliveryType = (cartList?.data?.details || []).filter(
  //   (item) => {
  //     if (deliveryType === "DOOR_STEP_DELIVERY") {
  //     }
  //     if (deliveryType === "PICK_UP") {
  //       return item?.products?.product_tags?.pick_up === 1;
  //     }
  //     return true;
  //   }
  // );
  const productsBasedOnDeliveryType = filterProductsByDeliveryType(
    cartList?.data?.details || [],
    deliveryType,
  );
  const cartDisplayCount = getCartDisplayCount(productsBasedOnDeliveryType);
  let cartSubTotal = productsBasedOnDeliveryType.reduce(
    (sum, cart) => sum + +cart.product_discount_price * +cart.product_quantity,
    0,
  );

  const addonTotal = cartList?.data?.addons?.reduce(
    (sum, addon) => sum + +addon?.price * +addon?.quantity,
    0,
  );
  let subTotal = cartSubTotal + addonTotal;

  const productGstAmt = cartList?.data?.gst;
  const FREE_DELIVERY = +deliveryFeeData?.free_delivery;
  const gst = +productGstAmt;
  subTotal = subTotal - productGstAmt;
  const totalBillValue = Math.round(+subTotal + +gst + +deliveryFee);

  useEffect(() => {
    const deliveryFeeRate =
      FREE_DELIVERY == 1 ? 0 : +deliveryFeeData?.rate || 0;
    setDeliveryFee(deliveryFeeRate);
  }, [deliveryFeeData?.rate]);
  useEffect(() => {
    // setTotalBill(() => Math.round(+subTotal + +gst + +deliveryFee));
    setBackupTotalBill(totalBillValue);
  }, [totalBillValue, subTotal, gst, deliveryFee]);

  useEffect(() => {
    setUser((prev) => {
      return {
        ...prev,
        cartCount: +cartList?.data?.cart_count,
      };
    });
  }, [cartList]);

  useEffect(() => {
    if (selectedAddress?.customer_address_id) {
      setPrimaryAddress(selectedAddress);
      deliveryFeeDataRefetch(selectedAddress?.customer_address_id);
    }
  }, [selectedAddress?.customer_address_id]);

  const billDetailsResult = useMemo(
    () =>
      calculateTotalBill(
        cartList,
        applyDiscount,
        deliveryFeeData,
        deliveryType,
      ),
    [cartList, applyDiscount, deliveryFeeData, deliveryType],
  );
  useEffect(() => {
    setBillDetails(billDetailsResult);
  }, [billDetailsResult]);
  // ✅ No need to find coupon — use max_value stored during apply
  useEffect(() => {
    if (!applyDiscount?.code || !cartSubTotal) return;

    const minValue = Number(applyDiscount?.min_value ?? 0);
    const maxValue = applyDiscount?.max_value
      ? Number(applyDiscount.max_value)
      : null;

    if (+cartSubTotal < minValue) {
      setApplyDiscount({
        code: null,
        percentage: null,
        type: null,
        save: null,
        min_value: null,
        max_value: null,
      });
      toast.warning("Coupon removed — minimum order value not met!");
      return;
    }

    if (maxValue !== null && +cartSubTotal > maxValue) {
      setApplyDiscount({
        code: null,
        percentage: null,
        type: null,
        save: null,
        min_value: null,
        max_value: null,
      });
      toast.warning(`Coupon removed — valid only up to ₹${maxValue}!`);
    }
  }, [cartSubTotal, applyDiscount?.code]);

  const toggleSelecteAddressDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setSelectAddressDrawer(open);
  };
  const validateBeforeCheckout = () => {
    dialogRef.current.closeDialog();
    if (!primaryAddress?.customer_address_id) {
      toast.warning("Please select the delivery address");
      setSelectAddressDrawer(true);
      return false;
    }
    if (!tcAgree) {
      toast.warning("Agree with terms and condition");
      return false;
    }
    return true;
  };

  const checkoutHandler = async (btnType) => {
    const cartInActiveItems = productsBasedOnDeliveryType?.findIndex((item) => {
      if (item?.parent_id) {
        return false;
      }
      return item?.products?.active_status == 0;
    });
    const addonInActiveItems = cartList?.data?.addons?.findIndex((item) => {
      return item?.cart_addons?.active_status == 0;
    });
    if (cartInActiveItems >= 0 || addonInActiveItems >= 0) {
      setToggleModal(true);
      toast.warning(
        "Some items in your cart are not available. Please remove them to proceed.",
      );
    } else if (
      suggestAddon &&
      addonList?.data?.length > 0 &&
      btnType == "PROCEED"
    ) {
      addOnOpen();
    } else {
      try {
        setCheckoutLoader(true);
        if (validateBeforeCheckout()) {
          dialogRef.current.closeDialog();
          const params = {
            billing_name: user?.customer_name,
            billing_mobile_number: user?.customer_mobile,
            customer_address_id: primaryAddress?.customer_address_id || "",
            customer_id: user?.customer_id,
            order_sub_total: subTotal,
            deliver_fee_type: +deliveryFeeData?.free_delivery,
            deliver_fee: deliveryFee,
            actual_delivery_fee: deliveryFeeData?.rate || null,
            deliver_fee_gst: deliveryFeeData?.rate_gst_total || "",
            delivery_without_gst: deliveryFeeData.rate_without_gst || null,
            df_cgst_value: deliveryFeeData?.cgst_amount || "",
            df_sgst_value: deliveryFeeData?.sgst_amount || "",
            cgst_tax: null,
            cgst_value: cartList?.data?.cgst,
            sgst_tax: null,
            sgst_value: cartList?.data?.sgst,
            promo_code: applyDiscount?.code || "",
            // promo_code_value: applyDiscount?.save || 0,
            promo_code_value: billDetails?.dynamicCouponSave || 0,
            product_discount_value: billDetails?.productDiscountSave || 0,
            promo_code_percentage: applyDiscount?.percentage || "",
            promo_type: applyDiscount?.type || "",
            order_overall_totall: billDetails?.totalAmount,
            region_id: user?.region_id,
            razorpay_payment_id: "",
            slot: "STANDARD",
            email: "",
            shop_id: null,
            expected_delivery: moment().format("YYYY-MM-DD"),
            delivery_mode: "Door Step Delivery",
            payment_mode: "Online Payment",
            preparation: maxPreparation,
            billing_details: billDetails,
          };
          const orderDetailsBackup = {
            ...params,
            shop_id: null,
            expected_delivery: moment().format("YYYY-MM-DD"),
            email: "info@utsavpack.com",
            contact_mobile:
              primaryAddress?.billing_mobile || user?.customer_mobile,
            contact_name: primaryAddress?.billing_name || user?.customer_name,
            slot: "STANDARD",
            checkout_tracking_id: generateRandomNumber(),
            ...gstInvoiceDetails,
            platform: "WEB",
          };
          const res = await processOrderBackup(orderDetailsBackup);
          if (res && !res?.error) {
            const generateOrderParams = {
              amount: Math.round(billDetails?.totalAmount) * 100,
              // amount: 2 * 100,
              checkout_tracking_id: orderDetailsBackup?.checkout_tracking_id,
              region_id: user?.region_id,
            };
            const orderId = await generateOrderId(
              "orderIdGenerate",
              generateOrderParams,
            );
            if (orderId != -1) {
              const razorPayId = await triggerRazorPay(
                orderDetailsBackup,
                orderId,
                user,
              );
              if (razorPayId) {
                const upadatedPayload = {
                  ...orderDetailsBackup,
                  razorpay_payment_id: razorPayId,
                  order_tracking_id: orderId,
                };

                const response = await successOrder(upadatedPayload);
                if (!response?.error) {
                  setUser((prev) => ({
                    ...prev,
                    cartCount: response?.data[0],
                  }));
                  setOrderId(response?.data[1]);
                  setApplyDiscount({
                    code: null,
                    percentage: null,
                    type: null,
                    save: null,
                    min_value: null,
                  });
                  handleCheckoutResultDialog();
                } else {
                  setErrorMsg(
                    response?.data?.message || "Something went wrong!!",
                  );
                  handleCheckoutResultDialog();
                }
              } else {
                toast.error("Payment ID - Failure");
              }
            } else {
              toast.error("Order Generation - Failure");
            }
          } else {
            toast.error("Order Pre Process - Failure");
          }
        }
      } catch (error) {
        // Handle the code here
        if (error?.error != "Payment popup closed by user") {
          if (error?.description || error) {
            toast.error(`ERROR: ${error?.description} || ${error}`);
          } else {
            toast.error("Unhandled Error");
          }
        }
      } finally {
        setCheckoutLoader(false);
      }
    }
  };
  const skipHandler = () => {
    setSuggestAddon(false);
    setShowSkip(false);
    handleAddonDialog();
    checkoutHandler("SKIP");
  };
  // console.log("billDetails before checkout:", billDetails);
  const itemCount =
    +cartList?.data?.details?.length + +cartList?.data?.addons?.length;
  const handleAddonDialog = () => dialogRef.current?.openDialog();
  const handleCheckoutResultDialog = () =>
    checkoutResultDialogRef.current?.openDialog();

  const offers = data?.data?.sections?.[1];

  const comboGroups = groupByParentId(productsBasedOnDeliveryType || []);
  const nonComboItems = productsBasedOnDeliveryType.filter(
    (item) => !item.parent_id,
  );
  const noteEligibleItems = productsBasedOnDeliveryType.filter(
    (item) => item?.products?.unit?.unit_name === "Kgs",
  );
  const firstExistingCartNote =
    noteEligibleItems.find((item) => item?.message_on_cake)?.message_on_cake ||
    "";

  const debouncedCartNoteUpdateRef = useRef(
    debounce(async (nextNote, items) => {
      if (!items?.length) return;

      try {
        const changedItems = items.filter(
          (item) => (item?.message_on_cake || "") !== nextNote,
        );
        if (!changedItems.length) return;

        await Promise.all(
          changedItems.map((item) =>
            updateCartMessage({
              cartId: item?.cart_id,
              message: nextNote,
            }),
          ),
        );
        refetch();
      } catch (error) {
        toast.error(error?.message || "Unable to update customer note");
      }
    }, 500),
  );

  useEffect(() => {
    return () => {
      debouncedCartNoteUpdateRef.current.cancel();
    };
  }, []);

  useEffect(() => {
    setCartCustomerNote(firstExistingCartNote);
    if (!hasInitializedCartNoteRef.current) {
      setIsCustomerNoteOpen(Boolean(firstExistingCartNote));
      hasInitializedCartNoteRef.current = true;
    }
  }, [firstExistingCartNote]);

  const closeUnavailableModal = () => setToggleModal(false);

  const handleCartCustomerNoteChange = (e) => {
    const nextNote = e.target.value;
    setCartCustomerNote(nextNote);
    debouncedCartNoteUpdateRef.current(nextNote, noteEligibleItems);
  };

  const isLoading = addonByRegionLoader || cartLoading || addressLoader;
  if (error) return <div>Error loading cart details</div>;
  if ((itemCount < 1 || itemCount == null) && !isLoading) {
    return <CartEmpty />;
  }

  const cartHasStreamProduct = cartList?.data?.details?.some((item) => {
    if (item?.parent_id) {
      // combo product → check parent_product
      return Number(item?.parent_product?.stream_engine) === 1;
    }
    // normal product → check products
    return Number(item?.products?.stream_engine) === 1;
  });

  const cartHasNormalProduct = cartList?.data?.details?.some((item) => {
    if (item?.parent_id) {
      return Number(item?.parent_product?.stream_engine) === 0;
    }
    return Number(item?.products?.stream_engine) === 0;
  });
  return (
    <>
      {(itemCount < 1 || itemCount == null) && !isLoading ? (
        <CartEmpty />
      ) : (
        <>
          <Box
            sx={{
              background: "var(--neutral-100)",
              pb: 5,
            }}
          >
            <Box sx={{ height: "3px", background: "linear-gradient(90deg, transparent 0%, var(--primary-red-base) 30%, var(--primary-red-300) 70%, transparent 100%)" }} />
            <CommonSection>
              <BreadcrumbNavigation items={[{ label: "Cart" }]} />
              <PromiseStrip>
                <PromiseItem>
                  <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />
                  Pan-India dispatch with protected packaging
                </PromiseItem>
                <PromiseItem>
                  <RoomOutlinedIcon sx={{ fontSize: 18 }} />
                  Destination chosen by delivery pincode
                </PromiseItem>
                <PromiseItem>
                  <VerifiedUserOutlinedIcon sx={{ fontSize: 18 }} />
                  Secure checkout with verified payment flow
                </PromiseItem>
              </PromiseStrip>
              <Grid container spacing={3}>
                <Grid
                  size={{ xs: 12, sm: 7, md: 8 }}
                  sx={{ gap: 2, display: "flex", flexDirection: "column" }}
                >
                  <Grid container spacing={3}>
                    <Grid
                      size={12}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {isLoading ? (
                        <DeliveryTypeSkeleton />
                      ) : (
                        <DeliveryCard
                          toggleSelecteAddressDrawer={
                            toggleSelecteAddressDrawer
                          }
                          primaryAddress={primaryAddress}
                          addressList={addressList}
                        />
                      )}
                    </Grid>
                  </Grid>

                  <Grid
                    size={12}
                    sx={{ gap: 2, display: "flex", flexDirection: "column" }}
                  >
                    <SectionHeading>
                      <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
                        Packaging Studio
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "var(--neutral-700)",
                        }}
                      >
                        Review products, quantities, and personalization before final review.
                      </Typography>
                    </SectionHeading>
                    <CartCard>
                      {isLoading ? (
                        <CartItemSkeleton />
                      ) : (
                        <>
                          {comboGroups.map((group, index) => (
                            <Box key={group.parent_id}>
                              <ParentComboProduct
                                key={group.parent_id}
                                cartLength={group.items.length}
                                index={index}
                                parentProduct={group?.parent_product}
                                childProducts={group.items}
                                isLoading={isLoading}
                                updateCartQtyMutate={updateCartQtyMutate}
                              />
                            </Box>
                          ))}
                          {nonComboItems.map((cart, index) => (
                            <CartItem
                              cartLength={nonComboItems.length}
                              index={index}
                              key={cart.cart_id}
                              cart={cart}
                              isLoading={isLoading}
                              updateCartQtyMutate={updateCartQtyMutate}
                              updateBulkQtyMutate={updateBulkQtyMutate}
                            />
                          ))}
                        </>
                      )}

                      {isLoading ? (
                        <CartItemSkeleton />
                      ) : (
                        cartList?.data?.addons?.length > 0 &&
                        cartList?.data?.addons?.map((addon, index) => (
                          <AddonItem
                            cartLength={cartList?.data?.addons?.length} // also pass .length not the array
                            addon={addon}
                            key={addon?.addon_id} // ← stable unique key
                            user={user}
                            updateAddonQtyMutate={updateAddonQtyMutate}
                          />
                        ))
                      )}
                    </CartCard>

                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems={"center"}
                      sx={{ my: { xs: 1, sm: 3 }, justifyContent: "center" }}
                    >
                      <Button
                        onClick={() => navigate("/")}
                        variant="outlined"
                        sx={{
                          py: 1.5,
                          fontSize: "16px",
                          border: "1px solid var(--primary-red-base)",
                          borderRadius: "999px",
                          px: 3,
                        }}
                      >
                        Explore more products
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12, sm: 5, md: 4 }} rowSpacing={3}>
                  <StickyActionPanel>
                    <BillingDetails
                      isLoading={isLoading}
                      itemCount={{
                        cart: getCartDisplayCount(
                          productsBasedOnDeliveryType.filter(
                            (item) => item?.is_bulk_product === 0,
                          ),
                        ),
                        bulk: productsBasedOnDeliveryType.filter(
                          (item) => item?.is_bulk_product > 0,
                        ).length,
                        addon: +cartList?.data?.addons?.length,
                      }}
                      deliveryType={deliveryType}
                      deliveryFeeData={deliveryFeeData}
                      billDetails={billDetails}
                      cartSubTotal={cartSubTotal}
                      backupTotalBill={backupTotalBill}
                      cartItems={productsBasedOnDeliveryType}
                    />
                    {noteEligibleItems?.length > 0 && (
                      <CartCustomerNoteCard>
                        <NoteToggleButton
                          type="button"
                          onClick={() =>
                            setIsCustomerNoteOpen((prevOpen) => !prevOpen)
                          }
                        >
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: "#614B25",
                            }}
                          >
                            Add note
                          </Typography>
                          <ExpandMoreRoundedIcon
                            sx={{
                              transform: isCustomerNoteOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              color: "#7D5C23",
                              fontSize: "20px",
                              flexShrink: 0,
                            }}
                          />
                        </NoteToggleButton>
                        <Collapse
                          in={isCustomerNoteOpen}
                          timeout="auto"
                          unmountOnExit
                        >
                          <TextField
                            fullWidth
                            value={cartCustomerNote}
                            onChange={handleCartCustomerNoteChange}
                            placeholder="Name / message / request"
                            variant="outlined"
                            inputProps={{ maxLength: CART_NOTE_MAX_LENGTH }}
                            sx={{
                              mt: 1,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "12px",
                                background: "rgba(255,255,255,0.92)",
                              },
                            }}
                          />
                        </Collapse>
                      </CartCustomerNoteCard>
                    )}
                    <TermsPanel>
                      <FormGroup sx={{ my: 0 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              onChange={(e) => setTcAgree(e.target.checked)}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "var(--neutral-700)",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              I agree to the {" "}
                              <Link
                                onClick={() => setTcOpen(true)}
                                rel="noopener"
                                sx={{
                                  marginLeft: "4px",
                                  marginRight: "4px",
                                  color: "var(--primary-red-base)",
                                  textDecoration: "underline",
                                  fontWeight: 600,
                                  "&:hover": {
                                    color: "var(--primary-red-dark)",
                                  },
                                }}
                              >
                                Terms & Conditions
                              </Link>
                            </Typography>
                          }
                          labelPlacement="end"
                          sx={{ margin: 0 }}
                        />
                      </FormGroup>
                    </TermsPanel>
                    <Stack
                      direction="row"
                      alignItems={"center"}
                      sx={{ gap: 2, mt: 1 }}
                    >
                      {!isLoading && (
                        <>
                          <Button
                            onClick={() => addOnOpen("ADDON_CARD")}
                            variant="outlined"
                            sx={{
                              py: 1.25,
                              fontSize: "16px",
                              width: "100%",
                              border: "1px solid var(--primary-red-base)",
                              borderRadius: "14px",
                            }}
                          >
                            Packaging upgrades
                          </Button>
                          <Button
                            disabled={!tcAgree}
                            onClick={() => checkoutHandler("PROCEED")}
                            variant="contained"
                            sx={{
                              py: 1.25,
                              fontSize: "16px",
                              width: "100%",
                              borderRadius: "14px",
                            }}
                          >
                            {checkoutLoader ? "Preparing..." : "Secure order review"}
                          </Button>
                        </>
                      )}
                    </Stack>
                    <TrustFooter>
                      Need help with quantity, custom print or dispatch timeline? Our support team can assist before payment.
                    </TrustFooter>
                  </StickyActionPanel>
                </Grid>
              </Grid>
            </CommonSection>
            {offers?.length > 0 && <SuggestedProducts data={offers} />}

            <AddonDialog
              ref={dialogRef}
              data={addonList?.data}
              setAddonItems={setAddonItems}
              addonItems={addonItems}
              showSkip={showSkip}
              setShowSkip={setShowSkip}
              refetch={refetch}
              skipHandler={skipHandler}
              cartHasStreamProduct={cartHasStreamProduct}
              cartHasNormalProduct={cartHasNormalProduct}
            />
            <CartSideNav
              toggleSelecteAddressDrawer={toggleSelecteAddressDrawer}
              addressList={addressList}
              setPrimaryAddress={setPrimaryAddress}
              primaryAddress={primaryAddress}
              setSelectedAddress={setSelectedAddress}
              selectedAddress={selectedAddress}
              addressListFetch={addressListFetch}
              gstInvoiceDetails={gstInvoiceDetails}
              setGstInvoiceDetails={setGstInvoiceDetails}
            />
          </Box>
          <AddressDialog
            open={addressDialogOpen}
            setOpen={setAddressDialogOpen}
            addressListFetch={addressListFetch}
          />
        </>
      )}
      <CheckoutResult
        ref={checkoutResultDialogRef}
        data={{ orderId, errorMsg }}
      />
      <TcModal open={tcOpen} setOpen={setTcOpen} />
      <CustomModal
        open={toggleModal}
        setOpen={setToggleModal}
        title="Unavailable items in cart"
        description="Some items are not available right now. Remove unavailable items to continue checkout."
        handleConfirm={closeUnavailableModal}
        confirmBtnLaebel="OK"
        cancelBtnLabel="Close"
      />
      {checkoutLoader && <LogoLoader />}
    </>
  );
}

const CartCard = styled(Box)(({ theme }) => ({
  border: "1px solid rgba(183, 124, 10, 0.24)",
  borderTop: "4px solid rgba(188, 128, 15, 0.9)",
  borderRadius: "16px",
  padding: "22px",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,250,242,0.96) 100%)",
  boxShadow: "0 10px 24px rgba(173, 132, 61, 0.08)",
}));
const PromiseStrip = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
}));
const PromiseItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  border: "1px dashed rgba(188, 128, 15, 0.38)",
  borderRadius: "12px",
  padding: "10px 12px",
  background: "rgba(255, 249, 238, 0.8)",
  color: "#5B4A2B",
  fontSize: "13px",
  fontWeight: 600,
}));
const SectionHeading = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  paddingLeft: theme.spacing(0.5),
}));
const CartCustomerNoteCard = styled(Box)(({ theme }) => ({
  border: "1px dashed rgba(188, 128, 15, 0.36)",
  borderRadius: "14px",
  padding: theme.spacing(1.2, 1.25),
  background: "rgba(255, 249, 238, 0.88)",
}));
const NoteToggleButton = styled("button")(({ theme }) => ({
  border: "none",
  background: "transparent",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
}));
const StickyActionPanel = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.25),
  position: "sticky",
  top: 92,
}));
const TermsPanel = styled(Box)(({ theme }) => ({
  border: "1px solid rgba(183, 124, 10, 0.24)",
  borderRadius: "12px",
  padding: theme.spacing(1.25, 1.5),
  background: "rgba(255, 252, 247, 0.95)",
}));
const TrustFooter = styled(Box)(({ theme }) => ({
  fontSize: "12px",
  color: "#74654A",
  lineHeight: 1.5,
  borderLeft: "3px solid rgba(188, 128, 15, 0.55)",
  padding: theme.spacing(0.5, 0, 0.5, 1.5),
}));
const BillLabel = styled(Box)(({ theme }) => ({
  fontWeight: 400,
  fontSize: "16px",
  color: "var(--neutral-800)",
  textAlign: "left",
  lineHeight: "150%",
}));
const BillValue = styled(Box)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "16px",
  color: "var(--neutral-black)",
  textAlign: "right",
  lineHeight: "150%",
}));
